import React from "react";
import { AlertTriangle, Heart } from "lucide-react";

const GAUGE_SIZE = 108;
const STROKE = 10;

const toneText = (tone) =>
  tone === "low" ? "aud-up" : tone === "mid" ? "aud-warn" : tone === "high" ? "aud-down" : "aud-flat";

const pctLabel = (ratio) => (ratio != null ? `${Math.round(ratio * 100)}%` : "—");

const FollowerChurnGauge = ({ churn, compact = false }) => {
  if (!churn) return null;

  const gaugeSize = compact ? 84 : GAUGE_SIZE;
  const stroke = compact ? 8 : STROKE;
  const r = (gaugeSize - stroke) / 2;
  const cx = gaugeSize / 2;
  const cy = gaugeSize / 2 + r / 2;
  const halfC = Math.PI * r;
  const fillFrac = churn.windowChurn != null ? Math.min(1, Math.max(0, churn.windowChurn)) : 0;
  const arcClass =
    churn.windowTone === "low"
      ? "aud-gauge-arc-low"
      : churn.windowTone === "mid"
      ? "aud-gauge-arc-mid"
      : "aud-gauge-arc-high";

  const hasWindowData = churn.windowFollows > 0 || churn.windowUnfollows > 0;

  return (
    <section
      className={`aud-card${compact ? " aud-card-compact" : ""}`}
      aria-label="Churn gauge"
    >
      <span className="aud-kicker">Churn · 7D</span>
      {!compact ? (
        <p className="aud-caption">Unfollows for every follow you earn</p>
      ) : null}

      {!hasWindowData && churn.lifetimeFollows === 0 && churn.lifetimeUnfollows === 0 ? (
        <p className="aud-gauge-hint">No churn data yet.</p>
      ) : (
        <>
          <div className="aud-gauge-body">
            <svg
              className="aud-gauge-svg"
              width={gaugeSize}
              height={cy + stroke / 2}
              viewBox={`0 0 ${gaugeSize} ${cy + stroke / 2}`}
              role="meter"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={churn.windowChurn != null ? Math.round(fillFrac * 100) : 0}
              aria-label="Unfollow-to-follow ratio for the last seven days"
            >
              <path
                d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`}
                fill="none"
                className="aud-gauge-track"
                strokeWidth={stroke}
                strokeLinecap="round"
              />
              {hasWindowData && fillFrac > 0 ? (
                <path
                  d={`M ${cx - r} ${cy} A ${r} ${r} 0 0 1 ${cx + r} ${cy}`}
                  fill="none"
                  className={arcClass}
                  strokeWidth={stroke}
                  strokeLinecap="round"
                  strokeDasharray={`${halfC * fillFrac} ${halfC}`}
                />
              ) : null}
              {!compact ? (
                <text x={cx} y={cy - 6} textAnchor="middle" className="aud-gauge-center">
                  7D churn
                </text>
              ) : null}
            </svg>

            <div className="aud-gauge-readout">
              <strong className={`tabular-nums ${toneText(hasWindowData ? churn.windowTone : "flat")}`}>
                {hasWindowData ? pctLabel(churn.windowChurn) : "—"}
              </strong>
              <span>
                {hasWindowData
                  ? compact
                    ? `${churn.windowUnfollows} vs ${churn.windowFollows} this week`
                    : `${churn.windowUnfollows} unfollows vs ${churn.windowFollows} follows this week`
                  : "No follow activity this week"}
              </span>
            </div>
          </div>

          {!compact && hasWindowData && churn.lostPerTen != null ? (
            <p className="aud-gauge-hint">
              {churn.windowTone === "high" ? (
                <AlertTriangle size={11} style={{ verticalAlign: "-2px", marginRight: 4 }} />
              ) : (
                <Heart size={11} style={{ verticalAlign: "-2px", marginRight: 4 }} />
              )}
              For every 10 follows this week, you lose{" "}
              <strong className={toneText(churn.windowTone)}>
                {churn.lostPerTen === 0 ? "almost none" : `~${churn.lostPerTen}`}
              </strong>
              {churn.windowTone === "low"
                ? " — retention is holding up."
                : churn.windowTone === "mid"
                ? " — worth keeping an eye on."
                : " — churn is eating your growth."}
            </p>
          ) : null}

          {!compact ? (
            <div className="aud-gauge-lifetime">
              <span>
                Lifetime · {churn.lifetimeUnfollows.toLocaleString()} unfollows /{" "}
                {churn.lifetimeFollows.toLocaleString()} follows
              </span>
              <strong className={`tabular-nums ${toneText(churn.lifetimeTone)}`}>
                {pctLabel(churn.lifetimeChurn)}
              </strong>
            </div>
          ) : null}
        </>
      )}
    </section>
  );
};

export default FollowerChurnGauge;
